import React, { useState, useEffect, useRef } from 'react';
import { Button, CheckChunkierIcon } from '@eventbrite/marmalade';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { usePicoXTrack } from '@eventbrite/bsp-sdk/react';
import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';
import { useUserData } from '@/hooks/useUserData/useUserData';
import { toPrivateEventId } from '@/utils/picoEventId';
import { PicoXFollowVenueEventNames, PICOX_TRACKING_ORIGIN, PICOX_SOURCE, type PicoXPayloadValue } from '@/tracking';
import styles from '../../_shared/buttons/FollowButton/FollowButton.module.scss';

export const FollowVenueButton = () => {
  const [followed, setFollowed] = useState(false);
  const hasFiredShown = useRef(false);
  const trackPicoX = usePicoXTrack();
  const { id: eventId, venue, organizer } = useBasicInfo();
  const { userId, isLoading: isUserDataLoading } = useUserData();

  const buildPayload = (): Record<string, PicoXPayloadValue> => ({
    tracking_origin: PICOX_TRACKING_ORIGIN,
    source: PICOX_SOURCE,
    eb_event_id: toPrivateEventId(eventId),
    venue_id: venue?.id ?? null,
    venue_name: venue?.name ?? null,
    user_id: userId,
    organizer_id: organizer?.id ?? null,
  });

  useEffect(() => {
    if (hasFiredShown.current) return;
    // Wait until user data has loaded so the snapshot records the real user_id
    // (useUserData resolves asynchronously via React Query).
    if (isUserDataLoading) return;
    hasFiredShown.current = true;
    trackPicoX(PicoXFollowVenueEventNames.Shown, buildPayload());
    // buildPayload intentionally excluded — snapshot once user data is resolved
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserDataLoading]);

  const handleClick = () => {
    const next = !followed;
    setFollowed(next);
    trackPicoX(next ? PicoXFollowVenueEventNames.Tapped : PicoXFollowVenueEventNames.Untapped, buildPayload());
  };

  return (
    <Button
      variant={followed ? 'secondary' : 'primary'}
      className={classNames(styles.followButton, styles.organizer, { [styles.following]: followed })}
      onClick={handleClick}
    >
      <div className={styles.textContainer}>
        {followed && <CheckChunkierIcon size="xsmall" />}
        {followed ? (
          <FormattedMessage id="Following" defaultMessage="Following" />
        ) : (
          <FormattedMessage id="Follow venue" defaultMessage="Follow venue" description="CTA to follow a venue" />
        )}
      </div>
    </Button>
  );
};
